import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../hooks/useApi';
import Icon from '../components/Icon';
import './ProductionSaucisses.css';

const EPICES_LABELS = [
  { key: 'kosher_salt_pct',  label: 'Kosher Salt' },
  { key: 'curing_salt_pct',  label: 'Pink Curing Salt #1' },
  { key: 'garlic_pct',       label: 'Granulated Garlic' },
  { key: 'onion_pct',        label: 'Granulated Onion' },
  { key: 'black_pepper_pct', label: 'Black Pepper 16 mesh' },
  { key: 'paprika_pct',      label: 'Paprika fumé' },
  { key: 'dry_milk_pct',     label: 'Dry Milk Powder' },
];

export default function RecetteSaucisses() {
  const [recette, setRecette] = useState(null);
  const [form, setForm]       = useState({});
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [msg, setMsg]         = useState('');

  useEffect(() => {
    Promise.all([
      api.get('/saucisses/recette'),
      api.get('/saucisses/batches'),
    ]).then(([rec, bts]) => {
      setRecette(rec);
      setForm(rec);
      setBatches(bts);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const handleSave = async () => {
    setSaving(true);
    setMsg('');
    try {
      const res = await api.put('/saucisses/recette', {
        kosher_salt_pct  : parseFloat(form.kosher_salt_pct)  || 1.60,
        curing_salt_pct  : parseFloat(form.curing_salt_pct)  || 0.25,
        garlic_pct       : parseFloat(form.garlic_pct)       || 0.45,
        onion_pct        : parseFloat(form.onion_pct)        || 0.45,
        black_pepper_pct : parseFloat(form.black_pepper_pct) || 0.35,
        paprika_pct      : parseFloat(form.paprika_pct)      || 0.25,
        dry_milk_pct     : parseFloat(form.dry_milk_pct)     || 2.50,
        prix_saucisse    : parseFloat(form.prix_saucisse)    || 4.00,
      });
      setRecette(res.recette);
      setMsg('success');
      setTimeout(() => setMsg(''), 3000);
    } catch (err) {
      setMsg('error:' + (err.message || 'inconnu'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="production-saucisses">
        <div style={{ padding: 'var(--space-7) 0' }}>
          <div className="skeleton" style={{ height: 40, maxWidth: 400, marginBottom: 'var(--space-3)' }} />
          <div className="skeleton" style={{ height: 200, marginBottom: 'var(--space-3)' }} />
        </div>
      </div>
    );
  }

  const isError = msg.startsWith('error:');
  const msgText = isError ? msg.replace('error:', '') : 'Recette sauvegardée avec succès.';

  return (
    <div className="production-saucisses">
      {/* En-tête */}
      <div className="page-header">
        <div>
          <h1 className="page-title">Recette — Saucisses Jalapeño Cheddar</h1>
        </div>
        <Link to="/production/saucisses" className="btn btn-ghost btn--sm">
          <Icon name="retour" size={13} /> Production
        </Link>
      </div>

      {/* Carte épices */}
      <div className="card" style={{ marginBottom: 'var(--space-5)' }}>
        <div className="card-header">
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <span style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center' }}>
              <Icon name="epices" size={16} />
            </span>
            <span className="card-title">Pourcentages d'épices (% du poids total viande)</span>
          </div>
        </div>

        <table className="esc-table" style={{ maxWidth: 500 }}>
          <thead>
            <tr>
              <th>Épice</th>
              <th style={{ width: 160 }}>% par défaut</th>
            </tr>
          </thead>
          <tbody>
            {EPICES_LABELS.map(e => (
              <tr key={e.key}>
                <td>{e.label}</td>
                <td>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      style={{
                        width: 90,
                        background: 'var(--bg)',
                        border: '1px solid var(--border)',
                        borderRadius: 'var(--radius-md)',
                        padding: '6px 10px',
                        color: 'var(--text)',
                        fontSize: 13,
                        outline: 'none',
                      }}
                      value={form[e.key] ?? ''}
                      onChange={ev => set(e.key, ev.target.value)}
                      onFocus={ev => (ev.target.style.borderColor = 'var(--accent)')}
                      onBlur={ev => (ev.target.style.borderColor = 'var(--border)')}
                    />
                    <span className="muted" style={{ fontSize: 12 }}>%</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Prix saucisse */}
        <div style={{ marginTop: 'var(--space-5)', paddingTop: 'var(--space-4)', borderTop: '1px solid var(--border)' }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-soft)', textTransform: 'uppercase', letterSpacing: '0.05em', display: 'block', marginBottom: 'var(--space-2)' }}>
            Prix par saucisse individuelle ($)
          </label>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
            <input
              type="number"
              min="0"
              step="0.25"
              style={{
                width: 100,
                background: 'var(--bg)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-md)',
                padding: '8px 12px',
                color: 'var(--text)',
                fontSize: 13,
                outline: 'none',
              }}
              value={form.prix_saucisse ?? ''}
              onChange={e => set('prix_saucisse', e.target.value)}
              onFocus={ev => (ev.target.style.borderColor = 'var(--accent)')}
              onBlur={ev => (ev.target.style.borderColor = 'var(--border)')}
            />
            <span className="muted" style={{ fontSize: 13 }}>
              Paquet (×4) : <strong className="gold" style={{ fontFamily: "'Cinzel', serif" }}>
                {(parseFloat(form.prix_saucisse || 4) * 4).toFixed(2)} $
              </strong>
            </span>
          </div>
        </div>

        {/* Message feedback */}
        {msg && (
          <div style={{ marginTop: 'var(--space-4)' }}>
            {isError
              ? <div className="error-inline"><Icon name="erreur" size={15} />{msgText}</div>
              : <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', padding: '10px 12px', borderRadius: 'var(--radius-md)', background: 'color-mix(in srgb, var(--success) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--success) 30%, transparent)', color: 'var(--success)', fontSize: 13 }}>
                  <Icon name="succes" size={15} />{msgText}
                </div>
            }
          </div>
        )}

        <div style={{ marginTop: 'var(--space-5)' }}>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving
              ? 'Sauvegarde...'
              : <><Icon name="sauvegarder" size={14} /> Sauvegarder comme recette par défaut</>}
          </button>
        </div>
      </div>

      {/* Historique batches */}
      {batches.length > 0 && (
        <div className="card rise-in">
          <div className="card-header">
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
              <span style={{ color: 'var(--muted)', display: 'flex', alignItems: 'center' }}>
                <Icon name="liste" size={16} />
              </span>
              <span className="card-title">Historique des batches</span>
            </div>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="esc-table">
              <thead>
                <tr>
                  <th>Batch</th>
                  <th>Date</th>
                  <th>Kosher Salt</th>
                  <th>Curing Salt</th>
                  <th>Garlic</th>
                  <th>Onion</th>
                  <th>Black Pepper</th>
                  <th>Paprika</th>
                  <th>Dry Milk</th>
                </tr>
              </thead>
              <tbody>
                {batches.map(b => (
                  <tr key={b.id}>
                    <td style={{ fontWeight: 600 }}>{b.nom}</td>
                    <td className="muted">{b.date_prod}</td>
                    <td>{b.kosher_salt_pct  != null ? `${b.kosher_salt_pct}%`  : '—'}</td>
                    <td>{b.curing_salt_pct  != null ? `${b.curing_salt_pct}%`  : '—'}</td>
                    <td>{b.garlic_pct       != null ? `${b.garlic_pct}%`       : '—'}</td>
                    <td>{b.onion_pct        != null ? `${b.onion_pct}%`        : '—'}</td>
                    <td>{b.black_pepper_pct != null ? `${b.black_pepper_pct}%` : '—'}</td>
                    <td>{b.paprika_pct      != null ? `${b.paprika_pct}%`      : '—'}</td>
                    <td>{b.dry_milk_pct     != null ? `${b.dry_milk_pct}%`     : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
